/**
 * BlogCategory — Category page for blog (blog.example.com/:locale/category/:slug)
 * Header with category info, grid of posts, sidebar with other categories
 */

import { T } from '../styles/tokens';
import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Button } from '../components/ui';

const API_BASE = '/api/v1/cms';

interface Post {
  id: string; slug: string; author_name: string; author_avatar: string; is_featured: number;
  cover_image: string; view_count: number; like_count: number;
  tags: string; metadata: string; published_at: string; title: string; excerpt: string;
  category_slug: string; category_color: string; category_icon: string; category_name: string;
}
interface Category { id: string; slug: string; icon: string; color: string; name: string; description: string; entry_count: number; }

function getLocale(): string {
  const path = window.location.pathname.split('/')[1];
  if (path && (path === 'pt-BR' || path === 'en' || path === 'es')) return path;
  return navigator.language?.startsWith('pt') ? 'pt-BR' : 'en';
}

function formatDate(d: string, locale: string): string {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
}

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' } as any,
  header: { padding: '20px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as any,
  logo: { fontSize: 20, fontWeight: 700, color: T.text, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 } as any,
  logoAccent: { background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  nav: { display: 'flex', gap: 16 } as any,
  navLink: { color: T.textSecondary, textDecoration: 'none', fontSize: 14 } as any,
  catHeader: { textAlign: 'center' as const, marginBottom: 48, padding: '40px 0' } as any,
  catTitle: { fontSize: 36, fontWeight: 800, lineHeight: 1.2, margin: '12px 0 16px' } as any,
  catDesc: { fontSize: 16, color: T.textSecondary, lineHeight: 1.6, maxWidth: 600, margin: '0 auto' } as any,
  catAccent: (color: string) => ({ width: 60, height: 4, borderRadius: 2, background: color, margin: '0 auto 16px' }) as any,
  layout: { display: 'flex', gap: 40 } as any,
  main: { flex: 1, minWidth: 0 } as any,
  sidebar: { width: 300, flexShrink: 0 } as any,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24, marginBottom: 40 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s, border-color 0.2s' } as any,
  cardImg: { width: '100%', aspectRatio: '16/9', objectFit: 'cover' as const, display: 'block' } as any,
  cardImgPlaceholder: { width: '100%', aspectRatio: '16/9', background: 'linear-gradient(135deg, #1a1e3a 0%, #12162b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.bgSecondary, fontSize: 48 } as any,
  cardBody: { padding: '16px 20px 20px' } as any,
  cardTitle: { fontSize: 17, fontWeight: 700, lineHeight: 1.3, margin: '8px 0', color: T.text } as any,
  cardExcerpt: { fontSize: 14, color: T.textSecondary, lineHeight: 1.5, margin: '0 0 12px' } as any,
  cardMeta: { display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: T.textMuted } as any,
  badge: (color: string) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${color}20`, color }) as any,
  sidebarCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 20 } as any,
  sidebarTitle: { fontSize: 14, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: T.textSecondary } as any,
  catItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer', fontSize: 14 } as any,
  catDot: (color: string) => ({ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }) as any,
  catCount: { marginLeft: 'auto', fontSize: 12, color: T.textMuted } as any,
  loadMore: { display: 'block', width: '100%', padding: '12px', background: T.bgCardHover, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textSecondary, fontSize: 14, cursor: 'pointer', textAlign: 'center' as const, marginBottom: 40 } as any,
  skeleton: { background: `linear-gradient(90deg, ${T.bgCard} 25%, ${T.border} 50%, ${T.bgCard} 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 8 } as any,
  footer: { padding: '40px 0', borderTop: `1px solid ${T.border}`, textAlign: 'center' as const, color: T.textMuted, fontSize: 13 } as any,
  empty: { textAlign: 'center' as const, padding: 60, color: T.textMuted } as any,
};

function PostCard({ post, locale }: { post: Post; locale: string }) {
  return (
    <div style={S.card} onClick={() => { window.location.href = `/${locale}/${post.slug}`; }}
      onMouseOver={(e: any) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.borderColor = T.primaryBg; }}
      onMouseOut={(e: any) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = T.border; }}>
      {post.cover_image ? <img style={S.cardImg} src={post.cover_image} alt={post.title} loading="lazy" /> : <div style={S.cardImgPlaceholder} />}
      <div style={S.cardBody}>
        {post.category_name && <span style={S.badge(post.category_color || T.primary)}>{post.category_name}</span>}
        <div style={S.cardTitle}>{post.title}</div>
        <p style={S.cardExcerpt}>{post.excerpt?.substring(0, 150)}{(post.excerpt?.length || 0) > 150 ? '...' : ''}</p>
        <div style={S.cardMeta}>
          <span>{post.author_name || ''}</span>
          <span>{formatDate(post.published_at, locale)}</span>
          <span>{(() => { try { return JSON.parse(post.metadata || "{}").reading_time_min || 5; } catch { return 5; } })()} min</span>
        </div>
      </div>
    </div>
  );
}

export function BlogCategoryPage(_props: { path?: string; locale?: string; categorySlug?: string }) {
  const locale = _props.locale || getLocale();
  const categorySlug = _props.categorySlug || '';
  const [posts, setPosts] = useState<Post[]>([]);
  const [category, setCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/categories/${categorySlug}?contentType=blog&locale=${locale}`).then(r => r.json()),
      fetch(`${API_BASE}/categories?contentType=blog&locale=${locale}`).then(r => r.json()),
    ]).then(([catRes, allCatsRes]) => {
      const catData = catRes.data;
      if (catData) {
        const cat = catData.category || catData;
        setCategory({ id: cat.id, slug: cat.slug, icon: cat.icon, color: cat.color, name: cat.name, description: cat.description, entry_count: catData.total || 0 });
        setPosts(catData.entries || []);
        setTotal(catData.total || catData.entries?.length || 0);
        setOffset(20);
      }
      setCategories((allCatsRes.data?.categories || []).filter((c: Category) => c.slug !== categorySlug));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [locale, categorySlug]);

  const loadMore = () => {
    fetch(`${API_BASE}/categories/${categorySlug}?contentType=blog&locale=${locale}&limit=20&offset=${offset}`).then(r => r.json()).then(res => {
      setPosts(prev => [...prev, ...(res.data?.entries || [])]);
      setOffset(prev => prev + 20);
    });
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={{ ...S.skeleton, height: 120, marginBottom: 40 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            {[1, 2, 3, 4].map(i => <div key={i} style={{ ...S.skeleton, height: 300 }} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`@media (max-width: 768px) { .blog-layout { flex-direction: column !important; } .blog-sidebar { width: 100% !important; } .blog-grid { grid-template-columns: 1fr !important; } }`}</style>
      <div style={S.container}>
        {/* Header */}
        <div style={S.header}>
          <a href={`/${locale}`} style={S.logo}>
            <span style={S.logoAccent}>WhiteLabelClaw</span>
            <span style={{ color: T.textMuted, fontWeight: 400 }}>Blog</span>
          </a>
          <nav style={S.nav}>
            <a href={`/${locale}`} style={S.navLink}>{locale === 'pt-BR' ? 'Inicio' : 'Home'}</a>
          </nav>
        </div>

        {/* Category Header */}
        {category && (
          <div style={S.catHeader}>
            <div style={S.catAccent(category.color)} />
            <h1 style={{ ...S.catTitle, color: category.color }}>{category.name}</h1>
            {category.description && <p style={S.catDesc}>{category.description}</p>}
            <span style={S.badge(category.color)}>{total} {locale === 'pt-BR' ? 'artigos' : 'articles'}</span>
          </div>
        )}

        {/* Layout */}
        <div class="blog-layout" style={S.layout}>
          <div style={S.main}>
            {posts.length === 0 ? (
              <div style={S.empty}>{locale === 'pt-BR' ? 'Nenhum artigo nesta categoria ainda.' : 'No articles in this category yet.'}</div>
            ) : (
              <div class="blog-grid" style={S.grid}>
                {posts.map(post => <PostCard key={post.id} post={post} locale={locale} />)}
              </div>
            )}
            {posts.length < total && (
              <Button variant="ghost" onClick={loadMore} fullWidth style={S.loadMore}>{locale === 'pt-BR' ? 'Carregar mais' : 'Load more'}</Button>
            )}
          </div>

          {/* Sidebar */}
          <div class="blog-sidebar" style={S.sidebar}>
            <div style={S.sidebarCard}>
              <div style={S.sidebarTitle}>{locale === 'pt-BR' ? 'Outras Categorias' : 'Other Categories'}</div>
              {categories.map(cat => (
                <div key={cat.slug} style={S.catItem} onClick={() => { window.location.href = `/${locale}/${cat.slug}`; }}>
                  <span style={S.catDot(cat.color)} />
                  <span>{cat.name}</span>
                  <span style={S.catCount}>{cat.entry_count}</span>
                </div>
              ))}
              {categories.length === 0 && <div style={{ color: T.textMuted, fontSize: 13 }}>{locale === 'pt-BR' ? 'Nenhuma outra categoria' : 'No other categories'}</div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <p>{locale === 'pt-BR' ? '2026 WhiteLabelClaw. Todos os direitos reservados.' : '2026 WhiteLabelClaw. All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}
